import React, { useEffect, useState } from 'react';
import '../App.css';
import { FaChartBar } from 'react-icons/fa';

/* ==============================
   CONFIG
============================== */
const APP_ID = 123359;
const API_TOKEN = 'QIZukwLIDzClWEl';
const WS_URL = `wss://ws.binaryws.com/websockets/v3?app_id=${APP_ID}`;

const TARGET_APP_ID = 123359;
const USD_TO_KES = 129;

/* ==============================
   UTC HELPERS
============================== */
const formatUTC = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/* ==============================
   COMPONENT
============================== */
function Monthly() {
  const currentMonth = new Date().toISOString().slice(0, 7);

  const [commission, setCommission] = useState(null);
  const [todayCommission, setTodayCommission] = useState(null);
  const [yesterdayCommission, setYesterdayCommission] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [error, setError] = useState(null);

  /* Clear monthly commission while new month loads to prevent flash of real data */
  useEffect(() => {
    setCommission(null);
  }, [selectedMonth]);

  /* ==============================
     HELPERS
  ============================== */
  const filterBreakdown = (breakdown = []) =>
    breakdown.filter((item) => item.app_id === TARGET_APP_ID);

  const sumUsd = (breakdown = []) =>
    breakdown.reduce((sum, item) => sum + (item.app_markup_usd || 0), 0);

  const sumTrades = (breakdown = []) =>
    breakdown.reduce((sum, item) => sum + (item.transactions_count || 0), 0);

  /* ==============================
     MONTHLY COMMISSION
     (1st → END MONTH, UTC)
  ============================== */
  useEffect(() => {
    if (!selectedMonth) return;

    const ws = new WebSocket(WS_URL);
    ws.onopen = () => ws.send(JSON.stringify({ authorize: API_TOKEN }));

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);

      if (data.msg_type === 'authorize') {
        const [year, month] = selectedMonth.split('-');

        const from = `${year}-${month}-01 00:00:00`;
        const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
        const to = `${year}-${month}-${lastDay} 23:59:59`;

        ws.send(
          JSON.stringify({
            app_markup_statistics: 1,
            date_from: from,
            date_to: to,
          })
        );
      }

      if (data.msg_type === 'app_markup_statistics') {
        setCommission(data.app_markup_statistics || {});
        ws.close();
      }

      if (data.msg_type === 'error') {
        setError(data.error?.message || 'Unknown error');
        ws.close();
      }
    };

    return () => ws.close();
  }, [selectedMonth]);

  /* ==============================
     TODAY COMMISSION (UTC)
  ============================== */
  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    ws.onopen = () => ws.send(JSON.stringify({ authorize: API_TOKEN }));

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);

      if (data.msg_type === 'authorize') {
        const today = formatUTC(new Date());

        ws.send(
          JSON.stringify({
            app_markup_statistics: 1,
            date_from: `${today} 00:00:00`,
            date_to: `${today} 23:59:59`,
          })
        );
      }

      if (data.msg_type === 'app_markup_statistics') {
        setTodayCommission(data.app_markup_statistics || {});
        ws.close();
      }
    };

    return () => ws.close();
  }, []);

  /* ==============================
     YESTERDAY COMMISSION (UTC)
  ============================== */
  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    ws.onopen = () => ws.send(JSON.stringify({ authorize: API_TOKEN }));

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);

      if (data.msg_type === 'authorize') {
        const d = new Date();
        d.setUTCDate(d.getUTCDate() - 1);
        const yesterday = formatUTC(d);

        ws.send(
          JSON.stringify({
            app_markup_statistics: 1,
            date_from: `${yesterday} 00:00:00`,
            date_to: `${yesterday} 23:59:59`,
          })
        );
      }

      if (data.msg_type === 'app_markup_statistics') {
        setYesterdayCommission(data.app_markup_statistics || {});
        ws.close();
      }
    };

    return () => ws.close();
  }, []);

  /* ==============================
     DERIVED VALUES
  ============================== */
  const monthlyBreakdown = filterBreakdown(commission?.breakdown || []);
  let monthlyUsd = sumUsd(monthlyBreakdown);
  const monthlyTrades = sumTrades(monthlyBreakdown);

  const todayBreakdown = filterBreakdown(todayCommission?.breakdown || []);
  let todayUsd = sumUsd(todayBreakdown);
  const todayTrades = sumTrades(todayBreakdown);

  const yesterdayBreakdown = filterBreakdown(yesterdayCommission?.breakdown || []);
  let yesterdayUsd = sumUsd(yesterdayBreakdown);
  const yesterdayTrades = sumTrades(yesterdayBreakdown);

  /* ==============================
     APPLY 80% RULE
     Monthly → only 2026+
     Today/Yesterday → always
  ============================== */


  const share60 = monthlyUsd * 0.75;
  const share40 = monthlyUsd * 0.25;

  /* ==============================
     RENDER
  ============================== */
  return (
    <div className="app-wrapper">
      <div className="app-container">
        <h1>
          <FaChartBar /> Admin Dashboard
        </h1>

        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        />

        {!commission ? (
          <div className="loader-page">
            <div className="loader"></div>
            <p>Please wait...</p>
          </div>
        ) : (
          <div className="card-container">
            <div className="commission-card total-summary">
              <p className="text-header">This Month's commission</p>
              <p className="total-mark-kes">${monthlyUsd.toFixed(2)}</p>
              <p className="total-mark">
                KES {Math.round(monthlyUsd * USD_TO_KES).toLocaleString('en-KE')}
              </p>
              <p className="total-trades">{monthlyTrades} Trades</p>
            </div>

            <div className="commission-card today-summary">
              <p className="text-header">Today's earnings</p>
              <p className="usd-data">${todayUsd.toFixed(2)}</p>
              <p className="price-kes">
                KES {(todayUsd * USD_TO_KES).toLocaleString('en-KE')}
              </p>
              <p className="green-text">{todayTrades} Trades</p>
            </div>

            <div className="commission-card yesterday-summary">
              <p className="text-header">Yesterday's earnings</p>
              <p className="usd-data">${yesterdayUsd.toFixed(2)}</p>
              <p className="price-kes">
                KES {(yesterdayUsd * USD_TO_KES).toLocaleString('en-KE')}
              </p>
              <p className="green-text">{yesterdayTrades} Trades</p>
            </div>
            

            <div className="commission-card share-summary">
              <p className="text-header">Commission Share</p>

              <div className="share-columns">
                <div className="share-card">
                  <p className="text-header">75% Share</p>
                  <p className="share-60">${share60.toFixed(2)}</p>
                  <p className="kes-equiv">
                    KES {Math.round(share60 * USD_TO_KES).toLocaleString('en-KE')}
                  </p>
                </div>

                <div className="share-card">
                  <p className="text-header">25% Share</p>    
                  <p className="share-40">${share40.toFixed(2)}</p>
                  <p className="kes-equiv">
                    KES {Math.round(share40 * USD_TO_KES).toLocaleString('en-KE')}
                  </p>
                </div>
              </div>
            </div>
            
          </div>
        )}

        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );
}

export default Monthly;
